import { Domain } from "./domain"
import { EventBase } from "./eventBase"

const LicenseEvent = {
  LICENSE_PAIRED: "License Paired",
  PURCHASE_INITIATED: "Purchase Initiated",
  TRIAL_GRANTED: "Trial Granted",
} as const

type LicenseEvent = EnumValues<typeof LicenseEvent>

interface LicenseEventPayload extends Record<string, unknown> {
  /**
   * Not all license events have a license ID. If the event doesn't have a
   * license ID, then use `undefined` to make it explicit.
   */
  licenseId: string | undefined
}

abstract class LicenseEventBase<
  TPayload extends LicenseEventPayload
> extends EventBase<typeof Domain.LICENSE, LicenseEvent, TPayload> {
  constructor(payload: TPayload) {
    super({
      context: payload,
      contentId: payload.licenseId,
    })
  }

  get domain(): typeof Domain.LICENSE {
    return Domain.LICENSE
  }
}

interface LicensePairedEventPayload extends LicenseEventPayload {
  itemNumber: string
  userEmail: string
}

export class LicensePaired<
  TContext extends Record<string, unknown>
> extends LicenseEventBase<LicensePairedEventPayload> {
  constructor(payload: LicensePairedEventPayload, context: TContext) {
    super({ ...payload, ...context })
  }

  get eventName(): LicenseEvent {
    return LicenseEvent.LICENSE_PAIRED
  }
}

interface PurchaseInitiatedEventPayload extends LicenseEventPayload {
  itemNumber: string
  userEmail: string
}

export class PurchaseInitiated extends LicenseEventBase<PurchaseInitiatedEventPayload> {
  constructor(payload: PurchaseInitiatedEventPayload) {
    super(payload)
  }

  get eventName(): LicenseEvent {
    return LicenseEvent.PURCHASE_INITIATED
  }
}

export interface TrialGrantedEventPayload extends LicenseEventPayload {
  itemNumber?: string
  trialCode?: string
  userEmail: string
}

export class TrialGranted<
  TContext extends Record<string, unknown>
> extends LicenseEventBase<TrialGrantedEventPayload> {
  constructor(payload: TrialGrantedEventPayload, context: TContext) {
    super({ ...payload, ...context })
  }

  get eventName(): LicenseEvent {
    return LicenseEvent.TRIAL_GRANTED
  }
}
